<template>
  <Page>
    <!-- Welcome Card (only show if no project is selected) -->
    <Card v-if="!selectedProjectId" id="welcome-card">
      <template #header>
        <h1 class="text-xl font-bold text-primary flex items-center">
          <i class="fa-solid fa-user-circle mr-2"></i>
          Welcome, {{ userName }}!
        </h1>
      </template>
      <div class="flex items-center space-x-3 mb-2">
        <div class="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
          <i class="fa-solid fa-user text-primary text-lg"></i>
        </div>
        <div>
          <h2 class="text-lg font-semibold text-text">Please select a project to continue</h2>
          <p class="text-text-light mt-0.5 text-xs">You don't have a default project set. Select from the available
            projects
            below to get started with ADAM Cloud.</p>
        </div>
      </div>
      <div class="flex items-center text-xs text-accent mt-1">
        <i class="fa-solid fa-info-circle mr-1"></i>
        <span>You can change your project later from the navigation menu</span>
      </div>
    </Card>

    <!-- Project Selection Card (fills available space) -->
    <Card id="project-selection" class="flex flex-col flex-1 min-h-0 !p-0 overflow-hidden">
      <template #header>
        <h2 class="text-xl font-bold text-primary flex items-center">
          <i class="fa-solid fa-folder-open mr-2"></i>
          Available Projects
        </h2>
      </template>
      
      <!-- Grid layout: content area (1fr) + paginator (auto) -->
      <div class="grid grid-rows-[1fr_auto] h-full min-h-0">
        <!-- Content area that grows to fill available space -->
        <div class="overflow-hidden p-4">
          <!-- Search Bar -->
          <div class="relative w-96 mb-6">
            <input type="text" placeholder="Search projects..."
              class="w-full bg-background border-0 rounded-full pl-10 pr-4 py-3 focus:ring-2 focus:ring-primary focus:outline-none text-sm text-text"
              v-model="search">
            <div class="absolute left-3 top-3.5 text-secondary">
              <i class="fa-solid fa-search"></i>
            </div>
          </div>

          <!-- Table Header -->
          <div class="bg-background rounded-xl p-4 mb-4">
            <div class="grid grid-cols-12 gap-4 text-sm font-medium text-text-light">
              <div class="col-span-3">Project Name</div>
              <div class="col-span-1">Status</div>
              <div class="col-span-3">Project Owners</div>
              <div class="col-span-2">Created Date</div>
              <div class="col-span-2">Updated Date</div>
              <div class="col-span-1">Action</div>
            </div>
          </div>

          <!-- Project List with calculated height -->
          <div id="project-list" class="space-y-3 overflow-y-auto" style="height: calc(100% - 100px);">
            <template v-if="isLoading">
              <div v-for="n in 5" :key="n" class="bg-background border border-border-light rounded-lg p-4 animate-pulse">
                <div class="grid grid-cols-12 gap-4 items-center">
                  <div class="col-span-3 flex items-center space-x-3">
                    <div class="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <div class="w-6 h-6 rounded-full bg-primary/30"></div>
                    </div>
                    <div>
                      <div class="h-4 w-32 bg-primary/20 rounded mb-2"></div>
                      <div class="h-3 w-24 bg-primary/10 rounded"></div>
                    </div>
                  </div>
                  <div class="col-span-1">
                    <div class="h-4 w-14 bg-secondary/20 rounded-full"></div>
                  </div>
                  <div class="col-span-3">
                    <div class="flex items-center space-x-2">
                      <div v-for="m in 2" :key="m" class="w-6 h-6 rounded-full bg-accent/20"></div>
                      <div class="h-3 w-20 bg-accent/10 rounded"></div>
                    </div>
                  </div>
                  <div class="col-span-2">
                    <div class="h-3 w-16 bg-primary/10 rounded"></div>
                  </div>
                  <div class="col-span-2">
                    <div class="h-3 w-16 bg-primary/10 rounded"></div>
                  </div>
                  <div class="col-span-1">
                    <div class="h-7 w-16 bg-accent/20 rounded-full"></div>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <!-- No results message when search returns empty -->
              <div v-if="projects.length === 0 && search.trim() !== ''" 
                class="text-center py-12">
                <div class="text-text-light">
                  <i class="fa-solid fa-search text-4xl mb-4 opacity-50"></i>
                  <h3 class="text-lg font-semibold mb-2">No projects found</h3>
                  <p class="text-sm">No projects match your search for "{{ search }}"</p>
                  <p class="text-xs mt-2 opacity-75">Try adjusting your search terms</p>
                </div>
              </div>
              
              <div v-for="(project, idx) in projects" :key="project.id" :id="`project-item-${idx + 1}`"
                :class="[
                  'bg-background border rounded-lg p-4 hover:shadow-lg transition-all cursor-pointer group',
                  project.id === selectedProjectId 
                    ? 'border-primary bg-primary/5 shadow-md' 
                    : 'border-border-light hover:border-primary'
                ]"
                @click="project.id && selectProject(project.id)">
                <div class="grid grid-cols-12 gap-4 items-center">
                  <div class="col-span-3 flex items-center space-x-3">
                    <div class="relative w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <i :class="project.icon + ' text-primary'"></i>
                      <!-- Selected indicator -->
                      <div v-if="project.id === selectedProjectId" 
                        class="absolute -top-1 -right-1 w-4 h-4 bg-success rounded-full flex items-center justify-center">
                        <i class="fa-solid fa-check text-white text-xs"></i>
                      </div>
                    </div>
                    <div>
                      <h3 :class="[
                        'font-bold',
                        project.id === selectedProjectId ? 'text-primary font-extrabold' : 'text-primary'
                      ]">{{ project.name }}</h3>
                      <p class="text-text-light text-xs">{{ project.description }}</p>
                    </div>
                  </div>
                  <div class="col-span-1">
                    <span :class="projectStatusClass(project) + ' text-xs px-2 py-1 rounded-full font-medium'">{{
                      project.status }}</span>
                  </div>
                  <div class="col-span-3">
                    <div class="flex items-center gap-3 min-w-0">
                      <div class="flex items-center -space-x-1">
                        <MemberAvatar
                          v-for="(member, memberIdx) in visibleMembers(project)"
                          :key="member.userId ?? member.id ?? memberIdx"
                          :name="member.fullName || 'Unknown'"
                          :style="{ zIndex: MAX_VISIBLE_MEMBERS - memberIdx }"
                        />
                        <Avatar
                          v-if="extraMembersCount(project) > 0"
                          :label="`+${extraMembersCount(project)}`"
                          shape="circle"
                          class="bg-secondary text-text-inverse font-semibold !w-6 !h-6 !text-xs flex items-center justify-center ring-2 ring-background border border-background"
                          :style="{ zIndex: 0 }"
                        />
                      </div>
                      <span class="text-sm text-text-light">
                        {{ projectMemberNames(project) }}
                      </span>
                    </div>
                  </div>
                  <div class="col-span-2 text-sm text-text-light">{{ project.createdAt ? formatDateDisplay(project.createdAt) : 'N/A' }}</div>
                  <div class="col-span-2 text-sm text-text-light">{{ project.lastModifiedAt ? formatDateDisplay(project.lastModifiedAt) : 'N/A' }}</div>
                  <div class="col-span-1">
                    <button v-if="project.id === selectedProjectId"
                      class="bg-success text-white rounded-full px-4 py-1.5 text-sm font-medium opacity-100 transition-opacity">
                      <!-- <i class="fa-solid fa-check mr-1"></i> -->
                      Selected
                    </button>
                    <button v-else
                      class="bg-accent text-text-inverse rounded-full px-4 py-1.5 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Select
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Paginator row - takes only the space it needs -->
        <div class="border-t border-border">
          <Paginator :current-page="page" :page-count="totalPages" :page-size="pageSize" :total-items="totalProjects"
            item-label="projects" @update:currentPage="goToPage" @first="goToPage(1)" @prev="goToPage(page - 1)"
            @next="goToPage(page + 1)" @last="goToPage(totalPages)" />
        </div>
      </div>
    </Card>
  </Page>
</template>

<script setup lang="ts">
import Page from '@/shared/components/layouts/Page.vue';
import Card from '@/shared/components/layouts/Card.vue';
import Paginator from '@/shared/components/ui/Paginator.vue';
import { computed, onMounted, defineComponent, h, watch, mergeProps } from 'vue';
import Avatar from 'primevue/avatar';
import { useProfileStore } from '@/shared/stores/profile';
import { useProjectStore } from '@/shared/stores/project';
import { useUserAvatar } from '@/shared/composables/useUserAvatar';
import { useDateDisplay } from '@/shared/composables/useDateDisplay';
import router from '@/router';
import type { UiProject, UiProjectMember } from '@/shared/mappers';
import { ProjectRole } from '@/core/types/models/RoleTypes';

definePage({
  meta: {
    title: 'Select a Project',
    requiresProject: false,
  },
});

const profileStore = useProfileStore();
const projectStore = useProjectStore();
const { formatDateDisplay } = useDateDisplay();

const userName = computed(() => profileStore.profile?.name || 'User');
const search = computed({
  get: () => projectStore.search,
  set: (val: string) => projectStore.setSearch(val),
});

const projects = computed(() => projectStore.projects);

const isLoading = computed(() => projectStore.isLoading);

const page = computed(() => projectStore.page);
const pageSize = computed(() => projectStore.pageSize);
const totalProjects = computed(() => projectStore.totalProjects);
const totalPages = computed(() => {
  const total = totalProjects.value;
  const size = pageSize.value;
  if (!size || size <= 0) {
    return 1;
  }
  const pages = Math.ceil(total / size);
  return Math.max(pages, 1);
});

const DEFAULT_PAGE_SIZE = 5;

const MAX_VISIBLE_MEMBERS = 3;

function ownerMembers(project: UiProject): UiProjectMember[] {
  return project.members.filter(member => member.role === ProjectRole.ProjectOwner);
}

function visibleMembers(project: UiProject): UiProjectMember[] {
  return ownerMembers(project).slice(0, MAX_VISIBLE_MEMBERS);
}

function extraMembersCount(project: UiProject): number {
  return Math.max(ownerMembers(project).length - MAX_VISIBLE_MEMBERS, 0);
}

function projectMemberNames(project: UiProject): string {
  const owners = ownerMembers(project);
  const displayedOwners = owners.slice(0, MAX_VISIBLE_MEMBERS);
  if (owners.length === 0) {
    return 'No project owners';
  }

  const names = displayedOwners.map(member => {
    const name = (member.fullName ?? '').trim();
    return name.length > 0 ? name : 'Unknown';
  });

  const extra = Math.max(owners.length - displayedOwners.length, 0);
  return extra > 0 ? `${names.join(', ')} (and ${extra} more)` : names.join(', ');
}

function doFetchProjects() {
  projectStore.fetchProjects({ page: 1, search: search.value });
}
onMounted(() => {
  doFetchProjects();
});

function goToPage(newPage: number) {
  const size = pageSize.value || DEFAULT_PAGE_SIZE;
  const maxPage = Math.max(totalPages.value, 1);
  const clampedPage = Math.min(Math.max(newPage, 1), maxPage);
  if (clampedPage === page.value) {
    return;
  }
  projectStore.fetchProjects({ page: clampedPage, pageSize: size, search: search.value });
}

// Refetch when search criteria changes
watch(
  () => search.value,
  (newValue, oldValue) => {
    if (newValue === oldValue) {
      return;
    }
    const size = pageSize.value || DEFAULT_PAGE_SIZE;
    projectStore.fetchProjects({ page: 1, pageSize: size, search: newValue });
  }
);
const selectedProjectId = computed(() => projectStore.selectedProjectId);

async function selectProject(id: string): Promise<void> {
  await projectStore.selectProject(id);
  // TODO: Change route to project home/dashboard
  await router.push({ path: '/asset-hierarchy' });
}

// MemberAvatar subcomponent for colored avatar
const MemberAvatar = defineComponent({
  name: 'MemberAvatar',
  props: { name: { type: String, required: true } },
  setup(props, { attrs }) {
    const { initials, colorClass } = useUserAvatar(props.name);
    return () =>
      h(
        Avatar,
        mergeProps(
          {
            label: initials.value,
            shape: 'circle',
            class: [
              colorClass.value,
              '!w-6 !h-6 !text-xs !font-bold !text-white ring-2 ring-background border border-background',
            ],
          },
          attrs
        )
      );
  },
});

// Status badge color and text (mock logic)
function projectStatusClass(project: UiProject) {
  if (project.status === 'Active') return 'bg-success bg-opacity-10 text-success';
  return 'bg-secondary bg-opacity-10 text-secondary';
}

</script>

<style scoped>
/* Add any additional scoped styles here if needed */
</style>
